import { SlidersHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../Button';
import { Chip } from '../Chip';
import { SearchInput } from '../Input';
import { MenuGroup, MenuItem, Popover, usePopover } from '../Popover';
import { useUi, type Density } from '../../stores/ui';
import { formatNumber } from '../../lib/format';
import styles from './TableToolbar.module.css';

// The strip above a table: title, count, the active-filter chips, and the controls
// that shape the view.
//
// Filter state lives in the URL, always (06-qol §3 and §6), so this reads from and
// writes to typed search params rather than holding its own — which is what makes
// any list shareable by copying the address bar.
//
// Deliberately generic: a screen supplies its own filter controls through
// `filterMenu`. A shared component reaching into a feature folder for its options
// is a dependency running the wrong way (CLAUDE.md §11).

export interface ActiveFilter {
  readonly id: string;
  readonly label: string;
  readonly onRemove: () => void;
}

interface TableToolbarProps {
  readonly title: string;
  readonly total: number;
  /** e.g. "of 5,000" when a filter is narrowing the list. */
  readonly totalLabel?: string;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly filters: readonly ActiveFilter[];
  readonly onClearFilters: () => void;
  /** This screen's own filter controls, rendered inside the View menu. */
  readonly filterMenu?: ReactNode;
}

const DENSITY_LABELS: Readonly<Record<Density, string>> = {
  comfortable: 'Comfortable',
  compact: 'Compact',
};

export function TableToolbar({
  title,
  total,
  totalLabel,
  query,
  onQueryChange,
  filters,
  onClearFilters,
  filterMenu,
}: TableToolbarProps): ReactNode {
  const density = useUi((state) => state.density);
  const setDensity = useUi((state) => state.setDensity);
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();

  return (
    <div className={styles.toolbar}>
      <div className={styles.top}>
        <div className={styles.heading}>
          <h1 className={styles.title}>{title}</h1>
          {/* Counts are shown, not hinted, so nobody opens a screen to find out
              whether there is work in it. */}
          <span className={styles.total}>
            {formatNumber(total)}
            {totalLabel !== undefined && ` ${totalLabel}`}
          </span>
        </div>

        <div className={styles.controls}>
          <SearchInput
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onClear={() => onQueryChange('')}
            placeholder="Search"
            // `/` focuses the primary search on any list screen (06-qol §2).
            data-primary-search
          />
          <Button
            ref={triggerRef}
            variant="quiet"
            icon={SlidersHorizontal}
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={isOpen}
          >
            View
          </Button>
        </div>
      </div>

      {filters.length > 0 && (
        <div className={styles.filters}>
          <span className={styles.filterLabel}>Filters</span>
          {filters.map((filter) => (
            <Chip
              key={filter.id}
              plain
              onRemove={filter.onRemove}
              removeLabel={`Remove ${filter.label}`}
            >
              {filter.label}
            </Chip>
          ))}
          <button type="button" className={styles.clearFilters} onClick={onClearFilters}>
            Clear all
          </button>
        </div>
      )}

      <Popover anchor={anchor} onClose={close} align="right" label="View options">
        <MenuGroup label="Density">
          {(['comfortable', 'compact'] as const).map((value) => (
            <MenuItem key={value} isSelected={density === value} onClick={() => setDensity(value)}>
              {DENSITY_LABELS[value]}
            </MenuItem>
          ))}
        </MenuGroup>
        {filterMenu}
      </Popover>
    </div>
  );
}
